<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { HeadingReorderDirection } from '@/services/headingService'
import type { CreateOrUpdateHeadingRequest, HeadingDTO } from '@/types'

import { useAppToast } from '@/composables/useAppToast'
import * as headingService from '@/services/headingService'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const { t } = useI18n()
const toast = useAppToast()

// Use global heading state
const {
  headings: globalHeadings,
  isLoading: globalIsLoading,
  refreshMetadata: refreshHeadings,
} = useMetadata()

// Local state (derived from global)
const headings = ref<HeadingDTO[]>([])
const isLoading = computed(() => globalIsLoading.value)

// Sync local state with global state
watch(
  () => globalHeadings.value,
  (newHeadings) => {
    headings.value = [...newHeadings]
  },
  { immediate: true },
)

const showDialog = ref(false)
const isEditing = ref(false)
const isSaving = ref(false)
const showDeleteDialog = ref(false)
const headingToDelete = ref<HeadingDTO | null>(null)
const headingToDeleteIsParent = ref(false)
const headingToDeleteDepth = ref(0)
const deleteLoading = ref(false)
const reorderingId = ref<number | null>(null)
const expandedKeys = ref<Record<string, boolean>>({})

interface HeadingTreeNode {
  key: string
  data: HeadingDTO & { depth: number }
  children?: HeadingTreeNode[]
}

// Form state
const formData = ref<CreateOrUpdateHeadingRequest>({
  nameDE: '',
  nameEN: '',
  abbreviation: '',
  order: 0,
  parentId: null,
})
const editingId = ref<number | null>(null)
const editingHasSubheadings = ref(false)
const editingDepth = ref(0)

// Computed
const dialogTitle = computed(() =>
  isEditing.value
    ? t('pages.admin.headings.dialog.titleEdit')
    : t('pages.admin.headings.dialog.titleCreate'),
)
const sortedHeadings = computed(() => [...headings.value].sort((a, b) => a.order - b.order))

// Convert headings to TreeNode format for TreeTable
// Depth: 0 = top-level, 1 = first subheading, 2 = second subheading (max)
function headingToTreeNode(heading: HeadingDTO, depth: number): HeadingTreeNode {
  const sortedChildren = heading.subHeadings
    ? [...heading.subHeadings].sort((a, b) => a.order - b.order)
    : []

  return {
    key: String(heading.id),
    data: {
      ...heading,
      depth,
    },
    children: sortedChildren.map((sub) => headingToTreeNode(sub, depth + 1)),
  }
}

const treeNodes = computed<HeadingTreeNode[]>(() => {
  return sortedHeadings.value.map((h) => headingToTreeNode(h, 0))
})
const columns = computed<TableColumn<HeadingTreeNode>[]>(() => [
  { id: 'order', header: t('pages.admin.headings.fields.order') },
  { id: 'abbreviation', header: t('pages.admin.headings.fields.abbreviation') },
  { id: 'name', header: t('common.labels.name') },
  { id: 'actions', header: t('common.labels.actions') },
])

// Available parents for selection
// - Can select top-level headings (depth 0) - creates depth 1 subheading
// - Can select depth-1 subheadings - creates depth 2 subheading
// - Cannot select depth-2 subheadings (would exceed max depth)
// - Cannot select the heading being edited or its descendants
interface ParentOption {
  id: number
  label: string
  depth: number
}

const availableParents = computed<ParentOption[]>(() => {
  const result: ParentOption[] = []

  function collectParents(heading: HeadingDTO, depth: number, prefix: string = '') {
    // Exclude the heading being edited (can't be its own parent)
    if (editingId.value !== null && heading.id === editingId.value) {
      return
    }

    // Only include if depth < 2 (so we can create children at depth 1 or 2)
    if (depth < 2) {
      result.push({
        id: heading.id,
        label: prefix
          ? `${prefix} > ${heading.abbreviation} - ${heading.nameEN}`
          : `${heading.abbreviation} - ${heading.nameEN}`,
        depth,
      })
    }

    // Recurse into subheadings
    if (heading.subHeadings && depth < 1) {
      const sortedSubs = [...heading.subHeadings].sort((a, b) => a.order - b.order)
      for (const sub of sortedSubs) {
        // Check if the sub is the heading being edited or is a descendant
        if (editingId.value !== null && sub.id === editingId.value) {
          continue
        }
        collectParents(sub, depth + 1, heading.abbreviation)
      }
    }
  }

  for (const heading of headings.value) {
    collectParents(heading, 0)
  }

  return result
})

// Methods
async function loadHeadings() {
  try {
    await refreshHeadings()
  } catch (err) {
    console.error('Error loading headings:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.admin.headings.messages.loadError'),
      duration: 3000,
    })
  }
}

function getDepthOfHeading(headingId: number): number {
  function findDepth(list: HeadingDTO[], depth: number): number {
    for (const h of list) {
      if (h.id === headingId) {
        return depth
      }
      if (h.subHeadings && h.subHeadings.length > 0) {
        const found = findDepth(h.subHeadings, depth + 1)
        if (found >= 0) {
          return found
        }
      }
    }
    return -1
  }
  return findDepth(headings.value, 0)
}

function getDefaultOrder(parentId: number | null): number {
  if (parentId === null) {
    // For top-level, find max order among top-level headings
    return Math.max(...headings.value.map((h) => h.order), 0) + 10
  }

  // Find the parent and get max order among its children
  function findParent(list: HeadingDTO[]): HeadingDTO | null {
    for (const h of list) {
      if (h.id === parentId) {
        return h
      }
      if (h.subHeadings) {
        const found = findParent(h.subHeadings)
        if (found) {
          return found
        }
      }
    }
    return null
  }

  const parent = findParent(headings.value)
  if (parent && parent.subHeadings && parent.subHeadings.length > 0) {
    return Math.max(...parent.subHeadings.map((s) => s.order), 0) + 10
  }
  return 10
}

function openCreateDialog(parentId: number | null = null) {
  isEditing.value = false
  editingId.value = null
  editingHasSubheadings.value = false
  editingDepth.value = parentId !== null ? getDepthOfHeading(parentId) + 1 : 0

  formData.value = {
    nameDE: '',
    nameEN: '',
    abbreviation: '',
    order: getDefaultOrder(parentId),
    parentId,
  }
  showDialog.value = true
}

function openEditDialog(node: HeadingTreeNode) {
  const heading = node.data
  isEditing.value = true
  editingId.value = heading.id
  editingDepth.value = heading.depth

  // Check if this heading has subheadings
  editingHasSubheadings.value = (heading.subHeadings?.length ?? 0) > 0

  formData.value = {
    nameDE: heading.nameDE,
    nameEN: heading.nameEN,
    abbreviation: heading.abbreviation,
    order: heading.order,
    parentId: heading.parentId,
  }
  showDialog.value = true
}

function closeDialog() {
  showDialog.value = false
  formData.value = {
    nameDE: '',
    nameEN: '',
    abbreviation: '',
    order: 0,
    parentId: null,
  }
  editingId.value = null
  editingHasSubheadings.value = false
}

async function saveHeading() {
  isSaving.value = true

  try {
    if (isEditing.value && editingId.value !== null) {
      await headingService.updateHeading(editingId.value, formData.value)
      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.admin.headings.messages.updateSuccess'),
        duration: 3000,
      })
    } else {
      await headingService.createHeading(formData.value)
      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.admin.headings.messages.createSuccess'),
        duration: 3000,
      })
    }

    await loadHeadings()
    closeDialog()
  } catch (err) {
    console.error('Error saving heading:', err)
    const errorMessage =
      err instanceof Error ? err.message : t('pages.admin.headings.messages.saveError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: errorMessage,
      duration: 5000,
    })
  } finally {
    isSaving.value = false
  }
}

function confirmDelete(node: HeadingTreeNode) {
  const heading = node.data
  headingToDelete.value = heading
  headingToDeleteDepth.value = heading.depth
  // Check if it's a parent with subheadings
  headingToDeleteIsParent.value = (heading.subHeadings?.length ?? 0) > 0
  showDeleteDialog.value = true
}

async function handleDelete() {
  if (!headingToDelete.value) {
    return
  }

  try {
    deleteLoading.value = true
    await headingService.deleteHeading(headingToDelete.value.id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.admin.headings.messages.deleteSuccess'),
      duration: 3000,
    })
    await loadHeadings()
    showDeleteDialog.value = false
    headingToDelete.value = null
  } catch (err) {
    console.error('Error deleting heading:', err)
    const errorMessage =
      err instanceof Error ? err.message : t('pages.admin.headings.messages.deleteError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: errorMessage,
      duration: 5000,
    })
  } finally {
    deleteLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadHeadings()
})

// Check if a heading is first among its siblings
function isFirstAmongSiblings(node: HeadingTreeNode): boolean {
  const heading = node.data
  const parentId = heading.parentId

  // Find siblings (headings with same parent)
  let siblings: HeadingDTO[]
  if (parentId === null) {
    // Top-level heading
    siblings = headings.value
  } else {
    // Find the parent and get its children
    const parent = findHeadingById(parentId, headings.value)
    siblings = parent?.subHeadings ?? []
  }

  if (siblings.length === 0) {
    return true
  }

  const sorted = [...siblings].sort((a, b) => a.order - b.order)
  return sorted[0]?.id === heading.id
}

// Check if a heading is last among its siblings
function isLastAmongSiblings(node: HeadingTreeNode): boolean {
  const heading = node.data
  const parentId = heading.parentId

  // Find siblings (headings with same parent)
  let siblings: HeadingDTO[]
  if (parentId === null) {
    // Top-level heading
    siblings = headings.value
  } else {
    // Find the parent and get its children
    const parent = findHeadingById(parentId, headings.value)
    siblings = parent?.subHeadings ?? []
  }

  if (siblings.length === 0) {
    return true
  }

  const sorted = [...siblings].sort((a, b) => a.order - b.order)
  return sorted[sorted.length - 1]?.id === heading.id
}

// Helper to find a heading by ID recursively
function findHeadingById(id: number, list: HeadingDTO[]): HeadingDTO | null {
  for (const h of list) {
    if (h.id === id) {
      return h
    }
    if (h.subHeadings) {
      const found = findHeadingById(id, h.subHeadings)
      if (found) {
        return found
      }
    }
  }
  return null
}

// Reorder a heading up or down
async function reorderHeading(node: HeadingTreeNode, direction: HeadingReorderDirection) {
  const heading = node.data
  reorderingId.value = heading.id

  try {
    await headingService.reorderHeading(heading.id, direction)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.admin.headings.messages.reorderSuccess'),
      duration: 2000,
    })
    await loadHeadings()
  } catch (err) {
    console.error('Error reordering heading:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.admin.headings.messages.reorderError'),
      duration: 5000,
    })
  } finally {
    reorderingId.value = null
  }
}
</script>

<template>
  <div class="admin-headings-page">
    <!-- Page Header -->
    <div class="mb-6 flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
          {{ t('pages.admin.headings.title') }}
        </h2>
        <p class="mt-1 text-gray-600 dark:text-gray-300">
          {{ t('pages.admin.headings.subtitle') }}
        </p>
      </div>
      <UButton
        icon="i-lucide-plus"
        :label="t('pages.admin.headings.actions.create')"
        color="neutral"
        @click="openCreateDialog()"
      />
    </div>

    <!-- TreeTable for hierarchical headings -->
    <div
      class="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100 shadow-sm dark:border-neutral-700 dark:bg-neutral-900"
    >
      <AppDataTableShell
        v-model:expanded="expandedKeys"
        :data="treeNodes"
        :columns="columns"
        :loading="isLoading"
        :get-row-id="(node: HeadingTreeNode) => node.key"
        :get-sub-rows="(node: HeadingTreeNode) => node.children"
      >
        <template #order-cell="{ row }">
          <span
            class="font-semibold"
            :class="row.original.data.depth === 0 ? 'text-[var(--app-text-primary)]' : 'text-muted'"
          >
            {{ row.original.data.order }}
          </span>
        </template>
        <template #abbreviation-cell="{ row }">
          <div class="flex items-center gap-1" :style="{ paddingLeft: `${row.depth}rem` }">
            <UButton
              v-if="row.getCanExpand()"
              :icon="row.getIsExpanded() ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
              color="neutral"
              size="xs"
              variant="ghost"
              :aria-label="row.original.data.abbreviation"
              @click.stop="row.toggleExpanded()"
            />
            <span v-else class="w-7 shrink-0" aria-hidden="true" />
            <code
              class="rounded px-2 py-1 text-xs"
              :class="
                row.original.data.depth === 0
                  ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                  : 'bg-neutral-200 text-muted dark:bg-neutral-700'
              "
            >
              {{ row.original.data.abbreviation }}
            </code>
          </div>
        </template>
        <template #name-cell="{ row }">
          <div class="flex flex-col">
            <span
              class="text-sm text-gray-900 dark:text-gray-100"
              :class="{ 'font-medium': row.original.data.depth === 0 }"
            >
              {{ row.original.data.nameDE }}
            </span>
            <span v-if="row.original.data.nameEN" class="text-sm text-gray-500 dark:text-gray-400">
              {{ row.original.data.nameEN }}
            </span>
          </div>
        </template>
        <template #actions-cell="{ row }">
          <div class="flex justify-end gap-1">
            <!-- Move up button -->
            <UButton
              v-app-tooltip.top="t('pages.admin.headings.actions.moveUp')"
              icon="i-lucide-arrow-up"
              color="neutral"
              size="sm"
              :disabled="isFirstAmongSiblings(row.original) || reorderingId !== null"
              :loading="reorderingId === row.original.data.id"
              :aria-label="t('pages.admin.headings.actions.moveUp')"
              @click.stop="reorderHeading(row.original, 'Up')"
              variant="ghost"
            />
            <!-- Move down button -->
            <UButton
              v-app-tooltip.top="t('pages.admin.headings.actions.moveDown')"
              icon="i-lucide-arrow-down"
              color="neutral"
              size="sm"
              :disabled="isLastAmongSiblings(row.original) || reorderingId !== null"
              :loading="reorderingId === row.original.data.id"
              :aria-label="t('pages.admin.headings.actions.moveDown')"
              @click.stop="reorderHeading(row.original, 'Down')"
              variant="ghost"
            />
            <!-- Add subheading button (only for depth 0 and 1, not depth 2) -->
            <UButton
              v-if="row.original.data.depth < 2"
              v-app-tooltip.top="t('pages.admin.headings.actions.addSubheading')"
              icon="i-lucide-plus"
              color="neutral"
              size="sm"
              :aria-label="t('pages.admin.headings.actions.addSubheading')"
              @click.stop="openCreateDialog(row.original.data.id)"
              variant="ghost"
            />
            <UButton
              icon="i-lucide-pencil"
              color="neutral"
              size="sm"
              :aria-label="t('common.actions.edit')"
              @click.stop="openEditDialog(row.original)"
              variant="ghost"
            />
            <UButton
              icon="i-lucide-trash"
              color="error"
              size="sm"
              :aria-label="t('common.actions.delete')"
              @click.stop="confirmDelete(row.original)"
              variant="ghost"
            />
          </div>
        </template>
        <template #empty>
          <div class="py-12 text-center">
            <div class="text-gray-500 dark:text-gray-400">
              <UIcon
                name="i-lucide-building-2"
                class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
              />
              <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                {{ t('pages.admin.headings.empty.title') }}
              </h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {{ t('pages.admin.headings.empty.subtitle') }}
              </p>
            </div>
          </div>
        </template>
      </AppDataTableShell>
    </div>

    <!-- Create/Edit Dialog -->
    <AppModal
      v-model:open="showDialog"
      :title="dialogTitle"
      :modal="true"
      :closable="!isSaving"
      :close-on-escape="!isSaving"
      :style="{ width: '32rem' }"
    >
      <div class="space-y-4">
        <!-- Parent Heading Selector -->
        <div class="flex flex-col gap-2">
          <label for="parentId" class="font-semibold">{{
            t('pages.admin.headings.fields.parent')
          }}</label>
          <AppSelect
            id="parentId"
            v-model="formData.parentId"
            :options="availableParents"
            option-value="id"
            option-label="label"
            :placeholder="t('pages.admin.headings.fields.parentPlaceholder')"
            :disabled="isSaving || editingHasSubheadings"
            class="w-full"
            show-clear
          />
          <small v-if="editingHasSubheadings" class="text-orange-700 dark:text-orange-400">
            {{ t('pages.admin.headings.fields.parentDisabledHasChildren') }}
          </small>
          <small v-else class="text-muted">{{ t('pages.admin.headings.fields.parentHelp') }}</small>
        </div>

        <!-- German Name -->
        <div class="flex flex-col gap-2">
          <label for="nameDE" class="font-semibold">{{
            t('pages.admin.headings.fields.nameDE')
          }}</label>
          <UInput
            id="nameDE"
            v-model="formData.nameDE"
            :placeholder="t('pages.admin.headings.fields.nameDEPlaceholder')"
            :disabled="isSaving"
            class="w-full"
          />
        </div>

        <!-- English Name -->
        <div class="flex flex-col gap-2">
          <label for="nameEN" class="font-semibold">{{
            t('pages.admin.headings.fields.nameEN')
          }}</label>
          <UInput
            id="nameEN"
            v-model="formData.nameEN"
            :placeholder="t('pages.admin.headings.fields.nameENPlaceholder')"
            :disabled="isSaving"
            class="w-full"
          />
        </div>

        <!-- Abbreviation -->
        <div class="flex flex-col gap-2">
          <label for="abbreviation" class="font-semibold">{{
            t('pages.admin.headings.fields.abbreviation')
          }}</label>
          <UInput
            id="abbreviation"
            v-model="formData.abbreviation"
            :placeholder="t('pages.admin.headings.fields.abbreviationPlaceholder')"
            :disabled="isSaving"
            class="w-full uppercase"
            @input="formData.abbreviation = formData.abbreviation.toUpperCase()"
          />
          <small class="text-muted">{{ t('pages.admin.headings.fields.abbreviationHelp') }}</small>
        </div>

        <!-- Order -->
        <div class="flex flex-col gap-2">
          <label for="order" class="font-semibold">{{
            t('pages.admin.headings.fields.order')
          }}</label>
          <UInputNumber
            id="order"
            v-model="formData.order"
            :disabled="isSaving"
            :min="0"
            :step="10"
            class="w-full"
            :increment="false"
            :decrement="false"
          />
          <small class="text-muted">{{ t('pages.admin.headings.fields.orderHelp') }}</small>
        </div>
      </div>

      <template #footer>
        <UButton
          :label="t('common.actions.cancel')"
          :disabled="isSaving"
          @click="closeDialog"
          variant="ghost"
        />
        <UButton
          :label="isEditing ? t('common.actions.update') : t('common.actions.create')"
          :loading="isSaving"
          :disabled="!formData.nameDE || !formData.nameEN || !formData.abbreviation"
          @click="saveHeading"
        />
      </template>
    </AppModal>

    <!-- Delete Confirmation Dialog -->
    <AppModal
      v-model:open="showDeleteDialog"
      :title="t('common.labels.confirmDeletion')"
      modal
      :style="{ width: '25rem' }"
    >
      <div class="flex items-center gap-4">
        <UIcon name="i-lucide-triangle-alert" class="text-3xl text-red-700 dark:text-red-400" />
        <div v-if="headingToDelete">
          <p>
            {{
              t('pages.admin.headings.dialog.confirmDelete', {
                name: headingToDelete.nameEN,
                abbreviation: headingToDelete.abbreviation,
              })
            }}
          </p>
          <p
            v-if="headingToDeleteIsParent"
            class="mt-2 text-sm text-orange-700 dark:text-orange-400"
          >
            {{ t('pages.admin.headings.dialog.hasSubheadingsWarning') }}
          </p>
        </div>
      </div>

      <template #footer>
        <UButton
          :label="t('common.actions.cancel')"
          @click="showDeleteDialog = false"
          variant="ghost"
        />
        <UButton
          :label="t('common.actions.delete')"
          color="error"
          :loading="deleteLoading"
          @click="handleDelete"
        />
      </template>
    </AppModal>
  </div>
</template>

<style scoped>
.admin-headings-page {
  padding: 1.5rem;
}
</style>
